// hooks/useUpsertCctvCameraChannels.ts
import { useMutation } from '@tanstack/react-query';
import { produce } from 'immer';
import isEqual from 'fast-deep-equal';

import { ICameraCCTV, ICameraCCTVChannel } from '@api/models';
import { createCctvCameraChannel, updateCctvCameraChannel } from '@api/services/cctv-camera.service';

export type CctvChannelPayload = Pick<
    ICameraCCTVChannel,
    'number' | 'hlsUrl' | 'webrtcUrl' | 'folder' | 'ptzControl'
> & {
    _id?: string;
    latestImage?: any;
    cameraCctv?: string | ICameraCCTV;
};

type ChannelsOnly = { channels: CctvChannelPayload[] };

// sanitize payload before sending to API
function sanitizeChannelForWrite(c: CctvChannelPayload) {
    return {
        number: c.number,
        hlsUrl: c.hlsUrl,
        webrtcUrl: c.webrtcUrl,
        folder: c.folder,
        ptzControl: {
            isAvailable: !!c.ptzControl?.isAvailable,
            deviceId: c.ptzControl?.deviceId ?? '',
        },
    };
}

function normForCompare(c?: CctvChannelPayload) {
    if (!c) return null;
    const { _id, number, hlsUrl, webrtcUrl, folder, ptzControl } = c;
    return {
        _id: _id ?? '',
        number: number?.toString?.() ?? '',
        hlsUrl: hlsUrl ?? '',
        webrtcUrl: webrtcUrl ?? '',
        folder: folder ?? '',
        ptzControl: {
            isAvailable: !!ptzControl?.isAvailable,
            deviceId: ptzControl?.deviceId ?? '',
        },
    };
}

function channelChanged(prev?: CctvChannelPayload, curr?: CctvChannelPayload) {
    if (!prev || !curr) return true;
    return !isEqual(normForCompare(prev), normForCompare(curr));
}

export default function useUpsertCctvCameraChannels(
    clientId: string,
    projectId: string,
    cctvCameraId: string,
    cctvCamera: ICameraCCTV
) {
    /**
     * UPDATE ONLY (existing channels with _id)
     */
    const updateMutation = useMutation({
        mutationFn: async (next: ChannelsOnly) => {
            const currentById = new Map(
                (cctvCamera.channels ?? [])
                    .filter((c): c is ICameraCCTVChannel => !!c && !!c._id)
                    .map((c) => [c._id, c])
            );

            const toUpdate: Array<{ id: string; payload: ReturnType<typeof sanitizeChannelForWrite> }> = [];

            for (const ch of next.channels) {
                if (!ch._id) continue; // only existing ones
                const prev = currentById.get(ch._id);
                if (channelChanged(prev as any, ch)) {
                    toUpdate.push({
                        id: ch._id,
                        payload: sanitizeChannelForWrite(ch),
                    });
                }
            }

            let latestChannelsFromServer: ICameraCCTVChannel[] | null = null;
            const updatedChannels: ICameraCCTVChannel[] = [];

            for (const { id, payload } of toUpdate) {
                // force to any first, avoid ICameraCCTV -> ICameraCCTVChannel cast error
                const res = (await updateCctvCameraChannel(
                    clientId,
                    projectId,
                    cctvCameraId,
                    id,
                    payload as any
                )) as any;

                if (Array.isArray(res?.channels)) {
                    const all = res.channels as ICameraCCTVChannel[];
                    latestChannelsFromServer = all;
                    const match = all.find((x) => x._id === id);
                    if (match) updatedChannels.push(match);
                } else if (res) {
                    updatedChannels.push(res as ICameraCCTVChannel);
                }
            }

            let finalChannels: ICameraCCTVChannel[];

            if (latestChannelsFromServer) {
                // backend returned canonical list
                finalChannels = latestChannelsFromServer;
            } else {
                const mergedById = new Map<string, ICameraCCTVChannel>();
                for (const c of cctvCamera.channels ?? []) if (c?._id) mergedById.set(c._id, c);
                for (const u of updatedChannels) if (u?._id) mergedById.set(u._id, u);
                finalChannels = Array.from(mergedById.values());
            }

            return produce(cctvCamera, (draft: ICameraCCTV & { channels?: ICameraCCTVChannel[] }) => {
                draft.channels = finalChannels;
            });
        },
    });

    /**
     * CREATE ONLY (new channels without _id)
     */
    const createMutation = useMutation({
        mutationFn: async (next: ChannelsOnly) => {
            const toCreate: ReturnType<typeof sanitizeChannelForWrite>[] = [];

            for (const ch of next.channels) {
                if (!ch._id) {
                    toCreate.push(sanitizeChannelForWrite(ch));
                }
            }

            if (toCreate.length === 0) {
                return cctvCamera;
            }

            let latestChannelsFromServer: ICameraCCTVChannel[] | null = null;
            const createdChannels: ICameraCCTVChannel[] = [];

            for (const payload of toCreate) {
                const res = (await createCctvCameraChannel(clientId, projectId, cctvCameraId, payload as any)) as any;

                if (Array.isArray(res?.channels)) {
                    const all = res.channels as ICameraCCTVChannel[];
                    latestChannelsFromServer = all;
                    createdChannels.push(...all);
                } else if (res) {
                    createdChannels.push(res as ICameraCCTVChannel);
                }
            }

            let finalChannels: ICameraCCTVChannel[];

            if (latestChannelsFromServer) {
                finalChannels = latestChannelsFromServer;
            } else {
                const existing = (cctvCamera.channels ?? []).filter((c): c is ICameraCCTVChannel => !!c && !!c._id);
                finalChannels = [...existing, ...createdChannels];
            }

            return produce(cctvCamera, (draft: ICameraCCTV & { channels?: ICameraCCTVChannel[] }) => {
                draft.channels = finalChannels;
            });
        },
    });

    return {
        // update existing channels (row Save)
        updateAsync: updateMutation.mutateAsync,
        isUpdating: updateMutation.isLoading,
        updateError: updateMutation.error,
        updateStatus: updateMutation.status,

        // create new channels (global Submit)
        createAsync: createMutation.mutateAsync,
        isCreating: createMutation.isLoading,
        createError: createMutation.error,
        createStatus: createMutation.status,
    };
}
